// components/ui/Input.tsx
import { type InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../lib/utils';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, icon, className, ...props }, ref) => {
    return (
      <div className="space-y-2">
        {label && (
          <label className="block text-label-md font-medium text-text">
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-text-muted">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            className={cn(
              'w-full h-11 px-4 py-3',
              'bg-surface text-text font-body text-body-md',
              'border-[1.5px] rounded-md',
              'placeholder:text-text-muted',
              'transition-all duration-fast',
              'hover:border-border-strong',
              'focus:border-primary focus:outline-none focus:shadow-glow',
              icon && 'pl-11',
              error
                ? 'border-error focus:border-error focus:shadow-[0_0_0_3px_rgba(199,91,91,0.15)]'
                : 'border-border',
              className
            )}
            {...props}
          />
        </div>
        {error && (
          <p className="text-body-sm text-error flex items-center gap-1.5">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </p>
        )}
        {hint && !error && (
          <p className="text-body-sm text-text-muted">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
